import { createConfig, http, fallback } from 'wagmi'
import { metaMask, walletConnect, coinbaseWallet, injected } from 'wagmi/connectors'
// The chain lists live in a LEAF module so the #144 recipient-code sweep can assert against them
// without loading createConfig/connectors — see the header of chainSets.ts. Do not re-introduce a
// chain-list literal in this file.
import {
  TESTNET_CHAINS,
  MAINNET_CHAINS,
  TESTNET_CHAINS_BY_KEY as T,
  MAINNET_CHAINS_BY_KEY as M,
} from '@/config/chainSets'
// Import-free by construction — see the zero-imports note in flags.ts. Importing anything that
// reaches smartAccount.ts from there would close the cycle wagmi → flags → smartAccount → wagmi
// while `wagmiConfig` below is still in its temporal dead zone.
import { isSmartWalletOfferEnabled } from '@/config/flags'

const projectId = import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID as string | undefined

const isTestnet = (import.meta.env.VITE_NETWORK || 'testnet') === 'testnet'

// Exported for consumer use (explorer URLs, chain name lookups).
export const chains = isTestnet ? TESTNET_CHAINS : MAINNET_CHAINS

const connectors = [
  metaMask(),
  injected({ shimDisconnect: true }),
  ...(projectId
    ? [walletConnect({
        projectId,
        // #170: pass explicit metadata so the EVM WalletConnect popup name is code-controlled
        // ('Parse DeFi') rather than falling back to document.title / the WC Cloud project name.
        metadata: {
          name: 'Parse DeFi',
          description: 'Cross-chain USDC bridge & swap aggregator',
          url: typeof window !== 'undefined' ? window.location.origin : 'https://parsedefi.com',
          icons: [
            typeof window !== 'undefined'
              ? `${window.location.origin}/favicon-192x192.png`
              : 'https://parsedefi.com/favicon-192x192.png',
          ],
        },
      })]
    : []),
  // #137 Phase 1b. `'all'` lets Coinbase offer the Base Account (passkey) option; `'eoaOnly'` hides
  // it. Evaluated once, at connector construction — matching how the rest of this file reads env.
  // The flag is AND-guarded by the Phase 1 routing flag, so this can never reach `'all'` on a build
  // that cannot route a contract account to bridge(). Production omits both names from the
  // deploy-prod-fe.sh allowlist, so this resolves to `'eoaOnly'` there.
  coinbaseWallet({ appName: 'Parse DeFi', preference: isSmartWalletOfferEnabled() ? 'all' : 'eoaOnly' }),
]

/**
 * DEV-375 — retry, restated EXPLICITLY on every `fallback` below, at BOTH the levels that matter.
 *
 * `fallback` hands each child transport `retryCount: 0` (`clients/transports/fallback.js:19` and the
 * `transports.map(fn => fn({ chain, retryCount: 0 }))` beside it) — bare `http()` transports lose
 * their own backoff the moment they are wrapped in one. These values are viem's own `createTransport`
 * defaults (`retryCount = 3, retryDelay = 150`), pinned here rather than inherited so a future viem
 * default change cannot move them silently.
 *
 * ── finding #11 (honesty): TWO DIFFERENT THINGS, do not conflate them ─────────────────────────────
 * `fallback([...], FALLBACK_RETRY)` — the second arg here — retries at the FALLBACK level: only after
 * EVERY child has failed once does the whole chain re-enter from `transports[0]`. That alone does NOT
 * "preserve today's exact behaviour" for a single transient failure: with every child pinned to
 * `retryCount: 0`, one 429/5xx from publicnode advances straight to the next (non-publicnode) entry
 * instead of backing off and retrying publicnode — exactly the endpoint class the pin exists to keep
 * using. Restoring that requires a SEPARATE, PER-CHILD override: the first (`publicnode`) entry of
 * every array below is `http(url, FALLBACK_RETRY)`, so a transient failure backs off and retries that
 * same endpoint up to 3 times before `fallback` ever advances to entry two.
 */
const FALLBACK_RETRY = { retryCount: 3, retryDelay: 150 } as const

// Create separate configs for testnet and mainnet to avoid type union issues. Each branch passes the
// CONCRETE tuple from chainSets — the union that used to break wagmi's strict chain-ID inference came
// from the `isTestnet ? … : …` ternary, not from the tuples themselves, so a shared constant is fine
// here as long as it is referenced inside a branch.
function buildConfig() {
  if (isTestnet) {
    return createConfig({
      chains: TESTNET_CHAINS,
      connectors,
      transports: {
        [T.ethereum.id]: http(),
        [T.base.id]: http(),
        [T.optimism.id]: http(),
        [T.arbitrum.id]: http(),
        [T.polygon.id]: http(),
        [T.bnb.id]: http(),
      },
    })
  }
  return createConfig({
    chains: MAINNET_CHAINS,
    connectors,
    transports: {
      // Explicit CORS-enabled public RPCs. Bare http() falls back to viem's
      // default public endpoints, which fail/rate-limit in-browser for these
      // chains → claimableBalance reads silently returned 0. publicnode serves
      // eth_call reliably with CORS.
      //
      // ── DEV-375: EACH IS NOW A `fallback`, AND publicnode STAYS FIRST ─────────────────────────
      // publicnode's endpoints serve every method this app uses EXCEPT
      // `eth_getTransactionReceipt`, which on base / optimism / arbitrum / bnb answers
      // `-32602 "Archive requests require a personal token"`. That is METHOD-specific and NOT
      // intermittent: `eth_blockNumber`, `eth_call`, `eth_getBalance` and
      // `eth_getTransactionByHash` all serve on the same host. viem maps -32602 to
      // `InvalidParamsRpcError`, and that error's message was reaching the user verbatim as a
      // terminal "Transfer Failed" on transfers that had in fact delivered.
      //
      // viem's `fallback` tries `transports[0]` for EVERY method and advances only on error
      // (`clients/transports/fallback.js:13-64`); -32602 is absent from its `shouldThrow`
      // stop-list (`:89-100`), so the second entry actually serves the receipt read. The cost is
      // one wasted round-trip per receipt read on the four gated chains — paid deliberately,
      // because REORDERING would change the endpoint that answers the balance / allowance /
      // `claimableBalance` reads publicnode was pinned for (the comment above records that prior
      // production defect: other endpoints rate-limited in-browser and `claimableBalance`
      // silently returned 0). Ordering is not a detail here; do not "optimise" it.
      //
      // Every second entry was measured keyless, `Access-Control-Allow-Origin: *`, and serving
      // `eth_getTransactionReceipt`. Measured FAILING and deliberately EXCLUDED: eth.llamarpc.com
      // and base.llamarpc.com (no CORS), rpc.ankr.com/eth (-32000), eth.drpc.org and
      // arbitrum.drpc.org (code 30), bsc.drpc.org (code 15), polygon-rpc.com (unparseable
      // chainId).
      //
      // NO KEYED URLS, EVER, IN THIS EVM TRANSPORTS MAP (I7). This bundle ships to browsers; an
      // embedded RPC key here is a public RPC key. That rule is REVIEWER-ENFORCED — there is NO
      // automated check for it in this file. Scoped deliberately to THIS map: the same bundle's
      // Solana transport (see the Solana RPC config, outside this file) DOES ship one keyed URL —
      // the Helius endpoint — as a stated exception. `scripts/deploy-prod-fe.sh:99-108` is a
      // CONTINUITY check on it, not a presence check on the build artifact: it reads the Helius URL
      // out of the bundle CURRENTLY LIVE at the apex (`:99-101`) and refuses to deploy if none is
      // found there (`:103`), or if the candidate value about to ship differs from that live one
      // (`:108`) — it never opens the built `apps/web/dist/` at all. So this is a next-run gate, not
      // a per-deploy artifact check: a refactor that drops `VITE_SOLANA_RPC_URL` from the build ships
      // ONE bundle with no Solana RPC to every prod user, uncaught, and only reds on the FOLLOWING
      // deploy once the live bundle it reads no longer contains the URL. The gate is real, and it
      // does refuse in both of those ways — this correction narrows WHAT it checks, not WHETHER it
      // exists. I7 is a claim about this EVM map only, not about the bundle as a whole.
      //
      // There used to be one. It was rewritten twice and then deleted, because each version was a
      // source-syntax blocklist and JS/TS has unbounded ways to reference a function, so a
      // blocklist can only ever close the instances someone already thought of:
      //   v1 matched only `http('...')` — a single-quoted literal glued to the callee. A keyed URL
      //      behind a CONST (`http(ETH_KEYED)`), a template literal, an env read, or `webSocket(...)`
      //      all shipped unnoticed, because an unmatched call site contributed nothing to check.
      //   v2 balance-scanned call arguments properly, but its call-site regex required the `(`
      //      adjacent to the name. ONE SPACE — `http (KEYED)` — or an alias
      //      (`const h = http; h(KEYED)`) both shipped a keyed URL with the full suite green.
      //   v3 added a whitespace-tolerant call-site scan plus a bare-mention check, but that
      //      bare-mention check exempted the whole import line — so `import { http as h } from
      //      'wagmi'` followed by `h(KEYED)` never spelled a bare `http`/`webSocket` anywhere v3
      //      looked, and shipped clean.
      // A fourth rewrite would close v3's escape and open a fifth: computed access
      // (`WAGMI['http']`), an identifier spelled with a Unicode escape for one of its letters, a
      // helper extraction, or an already-built transport imported from another module all
      // reference `http`/`webSocket` without spelling either name the way any source-syntax scan
      // can anchor on. A DEFECTIVE guard rates HIGH; an HONEST ABSENCE rates LOW — that ordering is
      // why deletion beat a fourth rewrite. But it was a REDUCTION IN COVERAGE, not a neutral swap:
      // v1's content check, gap-ridden as it was, still caught the likeliest accident shape — a
      // keyed URL pasted as a plain quoted literal, exactly the form every entry below takes — and
      // no content assertion of any kind survives in this file today; every assertion left in
      // wagmi.test.ts is structural (fallback counts, publicnode-first, per-endpoint retry,
      // robinhood single-endpoint, testnet-untouched). The ARTIFACT is not worse for it: the URLs
      // below are unchanged and were independently verified keyless by the security pass; keeping
      // them that way on every future edit is a review responsibility, not a test one. The
      // replacement, tracked as DEV-399, checks the BUILT BUNDLE rather than the source syntax — a
      // keyed URL lands in the bundle no matter which of the shapes above wrote it, so that check is
      // immune to this whole evasion class by construction.
      [M.ethereum.id]: fallback([
        http('https://ethereum-rpc.publicnode.com', FALLBACK_RETRY),
        http('https://cloudflare-eth.com'),
      ], FALLBACK_RETRY),
      [M.base.id]: fallback([
        http('https://base-rpc.publicnode.com', FALLBACK_RETRY),
        http('https://mainnet.base.org'),
        http('https://base.drpc.org'),
      ], FALLBACK_RETRY),
      [M.optimism.id]: fallback([
        http('https://optimism-rpc.publicnode.com', FALLBACK_RETRY),
        http('https://mainnet.optimism.io'),
        http('https://optimism.drpc.org'),
      ], FALLBACK_RETRY),
      [M.arbitrum.id]: fallback([
        http('https://arbitrum-one-rpc.publicnode.com', FALLBACK_RETRY),
        http('https://arb1.arbitrum.io/rpc'),
      ], FALLBACK_RETRY),
      [M.polygon.id]: fallback([
        http('https://polygon-bor-rpc.publicnode.com', FALLBACK_RETRY),
        http('https://polygon.drpc.org'),
      ], FALLBACK_RETRY),
      [M.bnb.id]: fallback([
        http('https://bsc-rpc.publicnode.com', FALLBACK_RETRY),
        http('https://bsc-dataseed.binance.org'),
      ], FALLBACK_RETRY),
      // Robinhood (#174) — MAINNET-ONLY. This bundle ships to browsers, so the RPC URL is the PUBLIC
      // RH endpoint (never a keyed/Alchemy URL, which would leak the key). Named explicitly rather
      // than bare http() for the reason in the comment above — viem's public fallback rate-limits.
      //
      // SINGLE ENDPOINT, and that is a stated GAP rather than an oversight (DEV-375): no second
      // public Robinhood RPC is known, and inventing one would be worse than none. Its measured
      // `eth_getTransactionReceipt` SERVES today, so it is not one of the four gated chains.
      [M.robinhood.id]: http('https://rpc.mainnet.chain.robinhood.com'),
    },
  })
}

export const wagmiConfig = buildConfig()
